import { useState, useRef, useEffect } from 'react'
import { Modal } from '@/shared/components/Modal'
import { Tooltip } from '@/shared/components/Tooltip'
import { useSidebar } from '@/context/SidebarContext'
import { api } from '@/lib/api'
import { trackEvent } from '@/lib/telemetry'
import { styles } from '@/shell/layout.styles'
import { NAV_ICONS } from '@/shell/nav/navIcons'

export function McpServerModal({ onClose }: { onClose: () => void }) {
  // Same self-contained pattern as CoderTemplatesModal: interest fires on the
  // explicit "Register interest" click (never on open), feedback is inline
  // because a toast can't paint over a native <dialog>. Fires both the
  // register_interest tracking event and the /api/interest POST (mirrors useComingSoon).
  const [state, setState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const mountedRef = useRef(true)
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false } }, [])

  async function register() {
    setState('submitting')
    setErrorMsg('')
    try {
      trackEvent('register_interest', 'mcp-server')
      await api.post('/api/interest/mcp-server')
      if (mountedRef.current) setState('success')
    } catch (err) {
      if (!mountedRef.current) return
      setState('error')
      setErrorMsg(err instanceof Error ? err.message : 'Network error. Please try again.')
    }
  }

  return (
    <Modal track="mcp_server" title="AI MCP Server" kicker="Coming soon" onClose={onClose} maxWidth={720}>
      <p className="guide-para">
        AI coding agents already write a lot of Terraform - and they do it blind. They guess
        at module inputs, pin whatever provider version they last saw, and only learn a policy
        exists when the plan gets rejected. An{' '}
        <a href="https://modelcontextprotocol.io" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none' }}>AI MCP server</a>{' '}
        would give Claude Code, Cursor, Copilot and any other agent a governed, read-mostly
        window into your org.
      </p>
      <div style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 6 }}>
        How it would work
      </div>
      <ul className="guide-list">
        <li><strong>Read modules, not guess them</strong> - list published modules, versions, inputs, outputs, README and deprecation status straight from the registry, so generated code references real, gated artefacts instead of hallucinated ones.</li>
        <li><strong>Understand state before touching it</strong> - inspect workspace state entries, resource addresses, outputs and lock status, so a refactor or import targets what actually exists rather than what the agent assumes.</li>
        <li><strong>Pin providers that will pass</strong> - query the provider versions your org serves, which builds carried CVE fixes, and their scan results and SBOMs, so the agent pins what's approved on the first try.</li>
        <li><strong>Metadata in context</strong> - ownership, scan verdicts, version history and the dependency graph flow into the agent's context, so it can explain <em>why</em> a module is deprecated or which workspaces a change will ripple into.</li>
        <li><strong>Draft Rego policies, never apply them</strong> - the one write path. Describe a rule in plain English ("no public S3 buckets", "tag everything with a cost centre"), the agent drafts the OPA policy, dry-runs it against recent plans, and submits it for a human to review and enable.</li>
        <li><strong>Scoped by the tokens you already issue</strong> - the server authenticates with your bot and personal access tokens, so an agent sees exactly what that token's role in the org allows and nothing more.</li>
      </ul>
      <div className="callout-accent">
        <strong>Exploratory.</strong> This isn't built yet. Register your interest so we
        can prioritise it.
      </div>
      {state === 'success' ? (
        <div style={{ marginTop: 16 }}>
          <div className="feedback-success">Thanks - we'll prioritise it.</div>
          <div className="modal-actions">
            <a className="btn btn-sm" href="https://modelcontextprotocol.io" target="_blank" rel="noreferrer">
              About MCP ↗
            </a>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
      ) : (
        <>
          {state === 'error' && (
            <div className="feedback-error" style={{ marginTop: 10 }}>{errorMsg}</div>
          )}
          <div className="modal-actions">
            <a className="btn btn-sm" href="https://modelcontextprotocol.io" target="_blank" rel="noreferrer">
              About MCP ↗
            </a>
            <button className="btn btn-primary btn-sm" onClick={register} disabled={state === 'submitting'}>
              {state === 'submitting' ? 'Registering…' : 'Register interest'}
            </button>
          </div>
        </>
      )}
    </Modal>
  )
}

export function McpServerNavButton() {
  const { collapsed } = useSidebar()
  const [hovered, setHovered] = useState(false)
  const [show, setShow] = useState(false)

  const open = () => setShow(true)

  return (
    <>
      {show && <McpServerModal onClose={() => setShow(false)} />}
      <Tooltip text="Let AI agents read your modules, state, providers and metadata over MCP - and draft Rego policies for review" placement="right">
        <button
          style={{ ...styles.navItem, ...(collapsed ? styles.navItemCollapsed : {}), ...(hovered ? styles.navItemHover : {}), width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
          onClick={open}
          aria-label={collapsed ? 'AI MCP Server' : undefined}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {NAV_ICONS.mcp}
          {!collapsed && 'AI MCP Server'}
        </button>
      </Tooltip>
    </>
  )
}

